import * as React from "react";
import {LoginContentAdminApi} from "../../../api/LoginContentAdminApi";
import {IToastNotification} from "../../../utils/planUserRemoval";
import {RaisedButton} from "../../common/RaisedButton";
import {prominentButtonStyle} from "../../common/buttonStyles";
import {useEffect, useState} from "react";
import {AdminRegion, RegionHero} from "../../../model/RegionLoginContent.model";

export interface AdminLoginHeroContentProps {
    activeRegion: AdminRegion;
    setToast: (toast: IToastNotification) => void;
    onHeroSave: (regionId: number) => void;
}

export const TAGLINE_DESCRIPTION = "Shown in small uppercase text above the headline";
export const TAGLINE_PLACEHOLDER = "Short tagline above the headline";
export const HEADLINE_PLACEHOLDER = "Main headline";
export const HERO_SAVE_BUTTON = "Save Hero Content";
export const HERO_CONTENT_SAVE_SUCCESS_TOAST = "Hero content saved.";
export const HERO_CONTENT_SAVE_FAILURE_TOAST = "Failed to save hero content.";
export const HERO_CONTENT_VALIDATION_TOAST = "Please fill in all required fields.";

interface FieldErrors {
    tagline: boolean;
    headline: boolean;
    description: boolean;
}

export const AdminLoginHeroContent: React.FunctionComponent<AdminLoginHeroContentProps> = (props) => {
    const [tagline, setTagline] = useState("");
    const [headline, setHeadline] = useState("");
    const [description, setDescription] = useState("");
    const [errors, setErrors] = useState<FieldErrors>({tagline: false, headline: false, description: false});

    useEffect(() => {
        if (props.activeRegion.hero) {
            setTagline(props.activeRegion.hero.tagline);
            setHeadline(props.activeRegion.hero.headline);
            setDescription(props.activeRegion.hero.description);
        }
        setErrors({tagline: false, headline: false, description: false});
    }, [props.activeRegion.id, props.activeRegion.hero]);

    const renderTagline = () => {
      return <div className="admin-login-content__form-group">
          <label className="admin-login-content__label" htmlFor="alc-hero-tagline">Tagline</label>
          <input
              id="alc-hero-tagline"
              type="text"
              className={`admin-login-content__input${errors.tagline ? " admin-login-content__input--invalid" : ""}`}
              value={tagline}
              onChange={e => {
                            setTagline(e.target.value);
                            setErrors(prev => ({...prev, tagline: false}));
                        }}
              placeholder={TAGLINE_PLACEHOLDER}
          />
          <span className="admin-login-content__form-hint">{TAGLINE_DESCRIPTION}</span>
      </div>;
    };

    const renderHeadline = () => {
        return <div className="admin-login-content__form-group">
            <label className="admin-login-content__label" htmlFor="alc-hero-headline">Headline</label>
            <input
                id="alc-hero-headline"
                type="text"
                className={`admin-login-content__input admin-login-content__input--large${errors.headline ? " admin-login-content__input--invalid" : ""}`}
                value={headline}
                onChange={e => {
                    setHeadline(e.target.value);
                    setErrors(prev => ({...prev, headline: false}));
                }}
                placeholder={HEADLINE_PLACEHOLDER}
            />
            <span className="admin-login-content__char-count">{headline.length} / 60</span>
        </div>;
    };

    const renderDescription = () => {
        return <div className="admin-login-content__form-group">
            <label className="admin-login-content__label" htmlFor="alc-hero-desc">Description</label>
            <textarea
                id="alc-hero-desc"
                className={`admin-login-content__textarea${errors.description ? " admin-login-content__textarea--invalid" : ""}`}
                value={description}
                onChange={e => {
                    setDescription(e.target.value);
                    setErrors(prev => ({...prev, description: false}));
                }}
                rows={3}
            />
            <span className="admin-login-content__char-count">
                    {description.length} / 300
                </span>
        </div>;
    };

    const saveHero = async () => {
        const newErrors: FieldErrors = {
            tagline: tagline.trim() === "",
            headline: headline.trim() === "",
            description: description.trim() === "",
        };
        if (Object.values(newErrors).some(Boolean)) {
            setErrors(newErrors);
            props.setToast({message: HERO_CONTENT_VALIDATION_TOAST, type: "error"});
            return;
        }
        const regionHero: RegionHero = {tagline, headline, description};
        LoginContentAdminApi.saveRegionHero(props.activeRegion.id, regionHero)
        .then(() => {
            props.setToast({message: HERO_CONTENT_SAVE_SUCCESS_TOAST, type: "success"});
            props.onHeroSave(props.activeRegion.id);
        })
        .catch(() => {
            props.setToast({message: HERO_CONTENT_SAVE_FAILURE_TOAST, type: "error"});
        });
    };

    const renderSaveButton = () => {
        return <div className="admin-login-content__actions">
            <RaisedButton
                type="button"
                className="admin-login-content__btn admin-login-content__btn--primary"
                style={prominentButtonStyle}
                primary={true}
                onClick={() => void saveHero()}
            >
                <i className="fa-solid fa-check" /> {HERO_SAVE_BUTTON}
            </RaisedButton>
        </div>;
    };

    return (
        <div className="admin-login-content__panel">
            {renderTagline()}
            {renderHeadline()}
            {renderDescription()}
            {renderSaveButton()}
        </div>
    );
};